/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useCreateAccountMappings } from "api/queries/user/useCreateAccountMappings";
import { useDeleteAccountMapping } from "api/queries/user/useDeleteAccountMapping";
import { useGetAccountMappingPaymentLocations } from "api/queries/user/useGetAccountMappingPaymentLocations";
import { useGetAccountMappings } from "api/queries/user/useGetAccountMappings";
import { useUpdateAccountMapping } from "api/queries/user/useUpdateAccountMapping";
import Alert from "components/v2/Alert/Alert";
import Card from "components/v2/Card/Card";
import LoadingSpinner from "components/v2/LoadingSpinner/LoadingSpinner";
import Tablefooter from "components/v2/Tablefooter/Tablefooter";
import errorToast from "helpers/errorToast";
import useConfirm from "hooks/useConfirm";
import usePermissions from "hooks/usePermissions";
import AccountMappingAddButton from "./AccountMappingAddButton";
import AccountMappingHeader from "./AccountMappingHeader";
import AccountMappingRow from "./AccountMappingRow";
import { EMPTY_ACCOUNT_MAPPING_ROW } from "./types";
import type { AccountMappingFormValues, AccountMappingRow as AccountMappingRowType } from "./types";
import "./AccountMapping.scss";

const DEFAULT_PAGE_SIZE = 10;

export default function AccountMapping() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { confirm, ConfirmModal } = useConfirm();

  const manageAccountMappings = useMemo(
    () => Boolean(JSON.parse(permissions || "{}")?.permissions?.admin_settings?.manage_account_mappings),
    [permissions],
  );

  useEffect(() => {
    if (permissions && !manageAccountMappings) {
      navigate("/settings/your-details");
    }
  }, [permissions, manageAccountMappings, navigate]);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const shouldReseed = useRef(true);
  const editingSnapshot = useRef<AccountMappingRowType | null>(null);

  const listFilters = useMemo(() => ({ page, page_size: itemsPerPage }), [page, itemsPerPage]);
  const { data, isLoading, isFetching, isError } = useGetAccountMappings(listFilters);
  const {
    paymentLocations,
    isLoading: isPaymentLocationsLoading,
    isFetching: isPaymentLocationsFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAccountMappingPaymentLocations();
  const paymentLocationOptions = useMemo(
    () => ({
      paymentLocations,
      isLoading: isPaymentLocationsLoading,
      isFetching: isPaymentLocationsFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    }),
    [
      paymentLocations,
      isPaymentLocationsLoading,
      isPaymentLocationsFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    ],
  );
  const createMappings = useCreateAccountMappings();
  const updateMapping = useUpdateAccountMapping();
  const deleteMapping = useDeleteAccountMapping();

  const methods = useForm<AccountMappingFormValues>({
    defaultValues: { mappings: [EMPTY_ACCOUNT_MAPPING_ROW] },
    shouldFocusError: true,
  });
  const { control, reset, getValues, clearErrors, handleSubmit, watch } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: "mappings" });
  const mappings = watch("mappings");

  useEffect(() => {
    if (!data || isFetching) return;
    if (!shouldReseed.current) return;

    const results = data.results || [];
    reset({
      mappings: results.length > 0 ? results : [{ ...EMPTY_ACCOUNT_MAPPING_ROW }],
    });
    setEditingIndex(null);
    editingSnapshot.current = null;
    shouldReseed.current = false;
  }, [data, isFetching, reset]);

  const cancelEdit = useCallback(() => {
    if (editingIndex === null || !editingSnapshot.current) {
      setEditingIndex(null);
      return;
    }
    const currentMappings = getValues("mappings");
    const nextMappings = currentMappings.map((row, index) => (index === editingIndex ? editingSnapshot.current! : row));
    reset({ mappings: nextMappings });
    setEditingIndex(null);
    editingSnapshot.current = null;
  }, [editingIndex, getValues, reset]);

  useEffect(() => {
    if (editingIndex === null) return undefined;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest("[data-account-mapping-row='editing']")) return;
      if (target.closest("[data-testid='account-mapping-save']")) return;
      if (target.closest(".cb-select__menu")) return;
      if (target.closest(".modal")) return;
      cancelEdit();
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [editingIndex, cancelEdit]);

  const savedCount = data?.count || 0;
  const hasUnsavedRows = (mappings || []).some((row) => !row.id);
  const showSave = editingIndex !== null || hasUnsavedRows;
  const isSaving = createMappings.isLoading || updateMapping.isLoading || deleteMapping.isLoading;
  const selectedPaymentLocations = (mappings || []).map((row) => row.payment_location).filter(Boolean);
  const totalPages = Math.max(1, Math.ceil(savedCount / itemsPerPage));

  const handlePageChange = (nextPage: number) => {
    shouldReseed.current = true;
    setEditingIndex(null);
    editingSnapshot.current = null;
    setPage(nextPage);
  };

  const handleItemsPerPageChange = (nextSize: number) => {
    shouldReseed.current = true;
    setEditingIndex(null);
    editingSnapshot.current = null;
    setItemsPerPage(nextSize);
    setPage(1);
  };

  const handleAdd = () => {
    if (editingIndex !== null) {
      cancelEdit();
    }
    append({ ...EMPTY_ACCOUNT_MAPPING_ROW });
  };

  const handleEdit = (index: number) => {
    if (editingIndex !== null) {
      cancelEdit();
    }
    editingSnapshot.current = { ...getValues(`mappings.${index}`) };
    setEditingIndex(index);
  };

  const submitMappings = async (formValues: AccountMappingFormValues) => {
    const currentMappings = formValues.mappings;

    if (editingIndex !== null) {
      const row = currentMappings[editingIndex];
      if (!row?.id) return;

      try {
        await updateMapping.mutateAsync({
          id: row.id,
          payload: {
            payment_location: row.payment_location,
            external_account_number: row.external_account_number.trim(),
          },
        });
        shouldReseed.current = true;
        setEditingIndex(null);
        editingSnapshot.current = null;
        toast.success(
          intl.formatMessage({
            id: "settings.accountMapping.updateSuccess",
            defaultMessage: "Account mapping updated.",
          }),
        );
      } catch (error) {
        errorToast(error);
      }
      return;
    }

    const newRows = currentMappings.filter((row) => !row.id);
    if (newRows.length === 0) return;

    try {
      await createMappings.mutateAsync(
        newRows.map((row) => ({
          payment_location: row.payment_location,
          external_account_number: row.external_account_number.trim(),
        })),
      );
      shouldReseed.current = true;
      toast.success(
        intl.formatMessage({
          id: "settings.accountMapping.saveSuccess",
          defaultMessage: "Account mappings saved.",
        }),
      );
    } catch (error) {
      errorToast(error);
    }
  };

  const handleSave = async () => {
    clearErrors("mappings");
    await handleSubmit(submitMappings)();
  };

  const handleDelete = async (index: number) => {
    const row = getValues(`mappings.${index}`);

    if (!row.id) {
      const unsavedCount = getValues("mappings").filter((item) => !item.id).length;
      const onlyUnsavedRows = getValues("mappings").every((item) => !item.id);
      if (onlyUnsavedRows && unsavedCount === 1) {
        reset({ mappings: [{ ...EMPTY_ACCOUNT_MAPPING_ROW }] });
        return;
      }
      remove(index);
      return;
    }

    const confirmed = await confirm({
      title: intl.formatMessage({
        id: "settings.accountMapping.removeTitle",
        defaultMessage: "Remove value?",
      }),
      message: intl.formatMessage({
        id: "settings.accountMapping.removeMessage",
        defaultMessage: "This action cannot be undone.",
      }),
      confirmBtnText: intl.formatMessage({ id: "general.remove", defaultMessage: "Remove" }),
    });

    if (!confirmed) return;

    try {
      await deleteMapping.mutateAsync(row.id);
      shouldReseed.current = true;
      if (editingIndex === index) {
        setEditingIndex(null);
        editingSnapshot.current = null;
      }
      toast.success(
        intl.formatMessage({
          id: "settings.accountMapping.deleteSuccess",
          defaultMessage: "Account mapping removed.",
        }),
      );
    } catch (error) {
      errorToast(error);
    }
  };

  if (permissions && !manageAccountMappings) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <Card borderRadius="md" shadow="xs" className="account-mapping-card">
        <AccountMappingHeader showSave={showSave} isSaving={isSaving} onSave={handleSave} />
        <Card.Body>
          <div className="account-mapping-body">
            {isError ? (
              <div className="account-mapping-error">
                <Alert variant="error" label={intl.formatMessage({ id: "general.error", defaultMessage: "Error" })}>
                  {intl.formatMessage({
                    id: "settings.accountMapping.loadError",
                    defaultMessage: "Failed to load account mappings. Please refresh the page and try again.",
                  })}
                </Alert>
              </div>
            ) : isLoading ? (
              <div className="account-mapping-loading">
                <LoadingSpinner size="32px" />
              </div>
            ) : (
              <>
                {fields.map((field, index) => (
                  <AccountMappingRow
                    key={field.id}
                    index={index}
                    row={mappings[index] ?? { ...EMPTY_ACCOUNT_MAPPING_ROW }}
                    isEditing={editingIndex === index}
                    showLabels={index === 0}
                    paymentLocationOptions={paymentLocationOptions}
                    selectedPaymentLocations={selectedPaymentLocations}
                    onEdit={() => handleEdit(index)}
                    onDelete={() => handleDelete(index)}
                  />
                ))}
                <AccountMappingAddButton onClick={handleAdd} />
              </>
            )}
          </div>
        </Card.Body>
        {savedCount > 0 && (
          <Tablefooter
            itemsPerPage={itemsPerPage}
            setItemsPerPage={handleItemsPerPageChange}
            totalPages={totalPages}
            totalData={savedCount}
            currentPage={page}
            pageClicked={handlePageChange}
          />
        )}
      </Card>
      <ConfirmModal />
    </FormProvider>
  );
}
